import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import type { TariffPublic,Tariffs } from '@cookspace-shared-contracts/core';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
    useCreateTariffMutation,
    useDeleteTariffMutation,
    useListTariffsQuery,
    useReorderTariffsMutation,
    useUpdateTariffMutation
} from '@/api/tariffsApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { SortableList } from '@/components/SortableList';
import { Switch } from '@/components/Switch';
import { Textarea } from '@/components/Textarea';

interface FormState {
    code: string;
    name: string;
    priceRub: string;
    durationDays: string;
    discountPercent: string;
    description: string;
    featuresText: string;
    sortOrder: string;
    isActive: boolean;
}

interface FormErrors {
    code?: string;
    name?: string;
    priceRub?: string;
    durationDays?: string;
    discountPercent?: string;
}

type DeleteIntent =
    | { kind: 'soft'; tariff: TariffPublic }
    | { kind: 'hard'; tariff: TariffPublic };

const EMPTY_FORM: FormState = {
    code: '',
    name: '',
    priceRub: '0',
    durationDays: '30',
    discountPercent: '0',
    description: '',
    featuresText: '',
    sortOrder: '0',
    isActive: true
};

const PRICE_FORMATTER = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
});

function formatPrice(rub: number): string {
    return PRICE_FORMATTER.format(rub);
}

function buildDeleteMessage(intent: DeleteIntent | null): string {
    if (!intent) {
        return '';
    }
    if (intent.kind === 'hard') {
        return `Полностью удалить тариф «${intent.tariff.name}»? Действие необратимо.`;
    }
    return `Деактивировать тариф «${intent.tariff.name}»? Его можно будет включить снова.`;
}

function formatDurationDays(days: number): string {
    const lastTwo = days % 100;
    const lastOne = days % 10;
    if (lastTwo >= 11 && lastTwo <= 14) {
        return `${days} дней`;
    }
    if (lastOne === 1) {
        return `${days} день`;
    }
    if (lastOne >= 2 && lastOne <= 4) {
        return `${days} дня`;
    }
    return `${days} дней`;
}

export function TariffsPage(): ReactElement {
    const [filterActiveOnly, setFilterActiveOnly] = useState(false);

    const queryArg: Tariffs.GetTariffsRequest = useMemo(() => {
        const arg: Tariffs.GetTariffsRequest = {};
        if (filterActiveOnly) {
            arg.isActive = true;
        }
        return arg;
    }, [filterActiveOnly]);

    const { data, isLoading, isError } = useListTariffsQuery(queryArg);

    const [createTariff, { isLoading: isCreating }] = useCreateTariffMutation();
    const [updateTariff, { isLoading: isUpdating }] = useUpdateTariffMutation();
    const [deleteTariff] = useDeleteTariffMutation();
    const [reorderTariffs] = useReorderTariffsMutation();

    const [isModalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TariffPublic | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<DeleteIntent | null>(null);

    const items = data?.items ?? [];

    const openCreate = (): void => {
        setEditing(null);
        setForm({ ...EMPTY_FORM, sortOrder: String(items.length) });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const openEdit = (tariff: TariffPublic): void => {
        setEditing(tariff);
        setForm({
            code: tariff.code,
            name: tariff.name,
            priceRub: String(tariff.priceRub),
            durationDays: String(tariff.durationDays),
            discountPercent: String(tariff.discountPercent),
            description: tariff.description ?? '',
            featuresText: tariff.features ? tariff.features.join('\n') : '',
            sortOrder: String(tariff.sortOrder),
            isActive: tariff.isActive
        });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const validate = (): boolean => {
        const errors: FormErrors = {};
        if (!form.code.trim()) {
            errors.code = 'Код обязателен';
        }
        if (!form.name.trim()) {
            errors.name = 'Название обязательно';
        }

        const price = Number(form.priceRub);
        if (!Number.isInteger(price) || price < 0) {
            errors.priceRub = 'Цена должна быть целым числом ≥ 0';
        }
        const duration = Number(form.durationDays);
        if (!Number.isInteger(duration) || duration < 1) {
            errors.durationDays = 'Длительность должна быть ≥ 1 дня';
        }
        const discount = Number(form.discountPercent);
        if (!Number.isInteger(discount) || discount < 0 || discount > 100) {
            errors.discountPercent = 'Скидка от 0 до 100';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setSubmitError(null);
        if (!validate()) {
            return;
        }

        const features = form.featuresText
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

        const sortOrderNum = Number(form.sortOrder);
        const body: Tariffs.CreateTariffRequest = {
            code: form.code.trim(),
            name: form.name.trim(),
            priceRub: Number(form.priceRub),
            durationDays: Number(form.durationDays),
            discountPercent: Number(form.discountPercent),
            isActive: form.isActive,
            sortOrder: Number.isFinite(sortOrderNum) ? sortOrderNum : 0
        };
        if (form.description.trim()) {
            body.description = form.description.trim();
        }
        if (features.length > 0) {
            body.features = features;
        }

        try {
            if (editing) {
                await updateTariff({ id: editing.id, body }).unwrap();
            } else {
                await createTariff(body).unwrap();
            }
            setModalOpen(false);
        } catch {
            setSubmitError('Не удалось сохранить тариф');
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        const intent = pendingDelete;
        setPendingDelete(null);
        try {
            if (intent.kind === 'soft') {
                await updateTariff({
                    id: intent.tariff.id,
                    body: { isActive: false }
                }).unwrap();
            } else {
                await deleteTariff({ id: intent.tariff.id }).unwrap();
            }
        } catch {
            setPageError(
                intent.kind === 'soft'
                    ? 'Не удалось деактивировать тариф'
                    : 'Не удалось удалить тариф'
            );
        }
    };

    const handleToggleActive = async (
        tariff: TariffPublic,
        next: boolean
    ): Promise<void> => {
        try {
            await updateTariff({ id: tariff.id, body: { isActive: next } }).unwrap();
        } catch {
            setPageError('Не удалось изменить статус');
        }
    };

    const handleReorder = async (orderedIds: string[]): Promise<void> => {
        try {
            await reorderTariffs({ orderedIds }).unwrap();
        } catch {
            setPageError('Не удалось изменить порядок');
        }
    };

    return (
        <div data-testid="tariffs-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Тарифы</h1>
                <button
                    type="button"
                    onClick={openCreate}
                    data-testid="tariff-create-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать тариф
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded border border-gray-200 bg-white p-3">
                <Switch
                    checked={filterActiveOnly}
                    onChange={setFilterActiveOnly}
                    label="Только активные"
                    testId="tariff-filter-active"
                />
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="tariffs-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="tariffs-error">
                    Не удалось загрузить тарифы
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="tariffs-table"
                >
                    <div className="grid grid-cols-[40px_120px_minmax(0,1fr)_100px_100px_80px_80px_80px_140px] gap-2 border-b border-gray-200 bg-gray-50 px-2 py-2 text-xs font-medium uppercase text-gray-600">
                        <span />
                        <span>Код</span>
                        <span>Название</span>
                        <span>Цена</span>
                        <span>Длительность</span>
                        <span>Скидка</span>
                        <span>Порядок</span>
                        <span>Активен</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Тарифы не найдены
                        </div>
                    )}

                    <SortableList
                        items={items}
                        onReorder={handleReorder}
                        testId="tariffs-sortable"
                        renderItem={(tariff) => (
                            <div
                                className="grid grid-cols-[120px_minmax(0,1fr)_100px_100px_80px_80px_80px_140px] items-center gap-2 border-b border-gray-100 px-2 py-2 text-sm last:border-b-0"
                                data-testid={`tariff-row-${tariff.id}`}
                            >
                                <span className="truncate font-mono text-xs text-gray-700">
                                    {tariff.code}
                                </span>
                                <span className="truncate text-gray-900">{tariff.name}</span>
                                <span className="text-gray-700">{formatPrice(tariff.priceRub)}</span>
                                <span className="text-gray-700">
                                    {formatDurationDays(tariff.durationDays)}
                                </span>
                                <span className="text-gray-700">
                                    {tariff.discountPercent > 0
                                        ? `${tariff.discountPercent}%`
                                        : '—'}
                                </span>
                                <span className="text-gray-700">{tariff.sortOrder}</span>
                                <span>
                                    <Switch
                                        checked={tariff.isActive}
                                        onChange={(next) => handleToggleActive(tariff, next)}
                                        testId={`tariff-active-${tariff.id}`}
                                    />
                                </span>
                                <span className="flex justify-end gap-1">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(tariff)}
                                        data-testid={`tariff-edit-${tariff.id}`}
                                        className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                        aria-label="Редактировать"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    {tariff.isActive ? (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setPendingDelete({ kind: 'soft', tariff })
                                            }
                                            data-testid={`tariff-delete-${tariff.id}`}
                                            className="rounded p-1.5 text-orange-600 transition hover:bg-orange-50"
                                            aria-label="Деактивировать"
                                            title="Деактивировать (soft-delete)"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setPendingDelete({ kind: 'hard', tariff })
                                            }
                                            data-testid={`tariff-hard-delete-${tariff.id}`}
                                            className="rounded p-1.5 text-red-600 transition hover:bg-red-50"
                                            aria-label="Удалить навсегда"
                                            title="Удалить навсегда"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </span>
                            </div>
                        )}
                    />
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? 'Редактировать тариф' : 'Новый тариф'}
                testId="tariff-modal"
            >
                <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-testid="tariff-form"
                >
                    <Input
                        label="Код"
                        value={form.code}
                        onChange={(e) => setForm({ ...form, code: e.target.value })}
                        error={formErrors.code}
                        placeholder="basic_30d"
                        data-testid="tariff-form-code"
                        autoFocus
                    />
                    <Input
                        label="Название"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        error={formErrors.name}
                        data-testid="tariff-form-name"
                    />
                    <div className="grid grid-cols-3 gap-3">
                        <Input
                            label="Цена (₽)"
                            type="number"
                            value={form.priceRub}
                            onChange={(e) => setForm({ ...form, priceRub: e.target.value })}
                            error={formErrors.priceRub}
                            data-testid="tariff-form-price"
                        />
                        <Input
                            label="Дней"
                            type="number"
                            value={form.durationDays}
                            onChange={(e) => setForm({ ...form, durationDays: e.target.value })}
                            error={formErrors.durationDays}
                            data-testid="tariff-form-duration"
                        />
                        <Input
                            label="Скидка (%)"
                            type="number"
                            value={form.discountPercent}
                            onChange={(e) =>
                                setForm({ ...form, discountPercent: e.target.value })
                            }
                            error={formErrors.discountPercent}
                            data-testid="tariff-form-discount"
                        />
                    </div>
                    <Textarea
                        label="Описание"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="tariff-form-description"
                    />
                    <Textarea
                        label="Преимущества (по одному в строке)"
                        rows={5}
                        value={form.featuresText}
                        onChange={(e) => setForm({ ...form, featuresText: e.target.value })}
                        data-testid="tariff-form-features"
                    />
                    <Input
                        label="Порядок сортировки"
                        type="number"
                        value={form.sortOrder}
                        onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                        data-testid="tariff-form-sort-order"
                    />
                    <Switch
                        checked={form.isActive}
                        onChange={(next) => setForm({ ...form, isActive: next })}
                        label="Активен"
                        testId="tariff-form-is-active"
                    />

                    {submitError !== null && submitError !== '' && (
                        <div className="text-sm text-red-600" data-testid="tariff-form-error">
                            {submitError}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setModalOpen(false)}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || isUpdating}
                            data-testid="tariff-form-submit"
                            className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            {editing ? 'Сохранить' : 'Создать'}
                        </button>
                    </div>
                </form>
            </Modal>

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title={pendingDelete?.kind === 'hard' ? 'Удалить тариф' : 'Деактивировать тариф'}
                message={buildDeleteMessage(pendingDelete)}
                confirmLabel={pendingDelete?.kind === 'hard' ? 'Удалить' : 'Деактивировать'}
                danger={pendingDelete?.kind === 'hard'}
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
